import * as Bixby from '$/core';
import dayjs from 'dayjs';

export abstract class GridService {
    
  public static buildManageCompanyCapitalStructureRows(
    captitalStructure: Bixby.Models.CompanyCapitalStructure,
    clientEbitda: number | null,
    isEditing: boolean
  ) {
    const buildCompanyLoanRow = (
      name: string | null,
      balance: number | null,
      spread: string | null,
      maturity: Date | null,
      agent: string | null
    ) => {
      const isVisible: boolean = name !== null && name !== undefined && name.length > 0;

      if (isVisible) {
        return {
          rowTitle: name,
          amount: balance,
          spread,
          maturity: maturity
            ? Bixby.Utils.formatMonthYear(dayjs.utc(maturity).toDate())
            : Bixby.Constants.MATURITY_EMPTY,
          agent,
          breakRow: false,
        } as Bixby.Models.CompanyCapitalStructureRow;
      } else {
        return null;
      }
    };

    const captitalStructureRows = [
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.Ebitda,
        rowTitle: '',
        cash: null,
        clientEbitda: isEditing ? clientEbitda : captitalStructure.clientEbitda,
        ebitda: captitalStructure.ebitda,
        adjustedEbitda: captitalStructure.adjustedEbitda,
        covenantEbitda: captitalStructure.covenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: '% Adjusted:',
        adjustedEbitda:
          captitalStructure.adjustedEbitda === null ||
          captitalStructure.adjustedEbitda === undefined ||
          captitalStructure.ebitda === null ||
          captitalStructure.ebitda === undefined ||
          captitalStructure.ebitda === 0
            ? null
            : captitalStructure.adjustedEbitda / captitalStructure.ebitda - 1,
        covenantEbitda:
          captitalStructure.covenantEbitda === null ||
          captitalStructure.covenantEbitda === undefined ||
          captitalStructure.ebitda === null ||
          captitalStructure.ebitda === undefined ||
          captitalStructure.ebitda === 0
            ? null
            : captitalStructure.covenantEbitda / captitalStructure.ebitda - 1,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      {
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.Cash,
        amount: captitalStructure.cash,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      buildCompanyLoanRow(
        captitalStructure.ablName,
        captitalStructure.ablAmount,
        captitalStructure.ablSpread,
        captitalStructure.ablMaturity,
        captitalStructure.ablAgent
      ),
      buildCompanyLoanRow(
        captitalStructure.priorityDebtName,
        captitalStructure.priorityDebtAmount,
        captitalStructure.priorityDebtSpread,
        captitalStructure.priorityDebtMaturity,
        captitalStructure.priorityDebtAgent
      ),
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      buildCompanyLoanRow(
        captitalStructure.revolverName,
        captitalStructure.revolverAmount || 0,
        captitalStructure.revolverSpread,
        captitalStructure.revolverMaturity,
        captitalStructure.revolverAgent
      ),
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      buildCompanyLoanRow(
        captitalStructure.firstLien1Name,
        captitalStructure.firstLien1Amount,
        captitalStructure.firstLien1Spread,
        captitalStructure.firstLien1Maturity,
        captitalStructure.firstLien1Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.firstLien2Name,
        captitalStructure.firstLien2Amount,
        captitalStructure.firstLien2Spread,
        captitalStructure.firstLien2Maturity,
        captitalStructure.firstLien2Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.firstLien3Name,
        captitalStructure.firstLien3Amount,
        captitalStructure.firstLien3Spread,
        captitalStructure.firstLien3Maturity,
        captitalStructure.firstLien3Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.firstLien4Name,
        captitalStructure.firstLien4Amount,
        captitalStructure.firstLien4Spread,
        captitalStructure.firstLien4Maturity,
        captitalStructure.firstLien4Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.firstLien5Name,
        captitalStructure.firstLien5Amount,
        captitalStructure.firstLien5Spread,
        captitalStructure.firstLien5Maturity,
        captitalStructure.firstLien5Agent
      ),
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.FirstLienDebtGross,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.FirstLienDebt,
        amount: captitalStructure.firstLienDebtGross,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.firstLienDebtGross, clientEbitda)
          : captitalStructure.firstLienDebtGrossClientEbitda,
        ebitda: captitalStructure.firstLienDebtGrossEbitda,
        adjustedEbitda: captitalStructure.firstLienDebtGrossAdjustedEbitda,
        covenantEbitda: captitalStructure.firstLienDebtGrossCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.FirstLienDebtNet,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.FirstLienDebtNet,
        amount: captitalStructure.firstLienDebtNet,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.firstLienDebtNet, clientEbitda)
          : captitalStructure.firstLienDebtNetClientEbitda,
        ebitda: captitalStructure.firstLienDebtNetEbitda,
        adjustedEbitda: captitalStructure.firstLienDebtNetAdjustedEbitda,
        covenantEbitda: captitalStructure.firstLienDebtNetCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      buildCompanyLoanRow(
        captitalStructure.secondLien1Name,
        captitalStructure.secondLien1Amount,
        captitalStructure.secondLien1Spread,
        captitalStructure.secondLien1Maturity,
        captitalStructure.secondLien1Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.secondLien2Name,
        captitalStructure.secondLien2Amount,
        captitalStructure.secondLien2Spread,
        captitalStructure.secondLien2Maturity,
        captitalStructure.secondLien2Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.secondLien3Name,
        captitalStructure.secondLien3Amount,
        captitalStructure.secondLien3Spread,
        captitalStructure.secondLien3Maturity,
        captitalStructure.secondLien3Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.secondLien4Name,
        captitalStructure.secondLien4Amount,
        captitalStructure.secondLien4Spread,
        captitalStructure.secondLien4Maturity,
        captitalStructure.secondLien4Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.secondLien5Name,
        captitalStructure.secondLien5Amount,
        captitalStructure.secondLien5Spread,
        captitalStructure.secondLien5Maturity,
        captitalStructure.secondLien5Agent
      ),
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.SecuredDebtGross,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.SecuredDebt,
        amount: captitalStructure.securedDebtGross,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.securedDebtGross, clientEbitda)
          : captitalStructure.securedDebtGrossClientEbitda,
        ebitda: captitalStructure.securedDebtGrossEbitda,
        adjustedEbitda: captitalStructure.securedDebtGrossAdjustedEbitda,
        covenantEbitda: captitalStructure.securedDebtGrossCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.SecuredDebtNet,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.SecuredDebtNet,
        amount: captitalStructure.securedDebtNet,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.securedDebtNet, clientEbitda)
          : captitalStructure.securedDebtNetClientEbitda,
        ebitda: captitalStructure.securedDebtNetEbitda,
        adjustedEbitda: captitalStructure.securedDebtNetAdjustedEbitda,
        covenantEbitda: captitalStructure.securedDebtNetCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      buildCompanyLoanRow(
        captitalStructure.otherDebt1Name,
        captitalStructure.otherDebt1Amount,
        captitalStructure.otherDebt1Spread,
        captitalStructure.otherDebt1Maturity,
        captitalStructure.otherDebt1Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.otherDebt2Name,
        captitalStructure.otherDebt2Amount,
        captitalStructure.otherDebt2Spread,
        captitalStructure.otherDebt2Maturity,
        captitalStructure.otherDebt2Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.otherDebt3Name,
        captitalStructure.otherDebt3Amount,
        captitalStructure.otherDebt3Spread,
        captitalStructure.otherDebt3Maturity,
        captitalStructure.otherDebt3Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.otherDebt4Name,
        captitalStructure.otherDebt4Amount,
        captitalStructure.otherDebt4Spread,
        captitalStructure.otherDebt4Maturity,
        captitalStructure.otherDebt4Agent
      ),
      buildCompanyLoanRow(
        captitalStructure.otherDebt5Name,
        captitalStructure.otherDebt5Amount,
        captitalStructure.otherDebt5Spread,
        captitalStructure.otherDebt5Maturity,
        captitalStructure.otherDebt5Agent
      ),
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.TotalDebtGross,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.TotalDebt,
        amount: captitalStructure.totalDebtGross,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.totalDebtGross, clientEbitda)
          : captitalStructure.totalDebtGrossClientEbitda,
        ebitda: captitalStructure.totalDebtGrossEbitda,
        adjustedEbitda: captitalStructure.totalDebtGrossAdjustedEbitda,
        covenantEbitda: captitalStructure.totalDebtGrossCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.TotalDebtNet,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.TotalDebtNet,
        amount: captitalStructure.totalDebtNet,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(captitalStructure.totalDebtNet, clientEbitda)
          : captitalStructure.totalDebtNetClientEbitda,
        ebitda: captitalStructure.totalDebtNetEbitda,
        adjustedEbitda: captitalStructure.totalDebtNetAdjustedEbitda,
        covenantEbitda: captitalStructure.totalDebtNetCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.InterestCoverage,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.InterestCoverage,
        amount: null,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(clientEbitda, captitalStructure.interestExpense)
          : captitalStructure.interestCoverageClientEbitda,
        ebitda: captitalStructure.interestCoverageEbitda,
        adjustedEbitda: captitalStructure.interestCoverageAdjustedEbitda,
        covenantEbitda: captitalStructure.interestCoverageCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        key: Bixby.Enums.CompanyCapitalStructureKeys.InterestCoverageAfterCapitalExpenditures,
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.InterestCoverageCapEx,
        amount: null,
        clientEbitda: isEditing
          ? Bixby.Utils.calculateMultiplierAsNumber(
              parseFloat((clientEbitda as any) || '0') -
                parseFloat((captitalStructure.capitalExpenditures as any) || '0'),
              captitalStructure.interestExpense
            )
          : captitalStructure.interestCoverageAfterCapitalExpendituresClientEbitda,
        ebitda: captitalStructure.interestCoverageAfterCapitalExpendituresEbitda,
        adjustedEbitda: captitalStructure.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
        covenantEbitda: captitalStructure.interestCoverageAfterCapitalExpendituresCovenantEbitda,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
        hasClientEbitda: true,
      },
      {
        rowTitle: null,
        amount: null,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: true,
      },
      {
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.CommittedCredit,
        amount: captitalStructure.lineOfCreditSize,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
      },
      {
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.AvailableCredit,
        amount: captitalStructure.lineOfCreditAvailability || 0,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
      },
      {
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.Cash,
        amount: captitalStructure.cash,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
      },
      {
        rowTitle: Bixby.Enums.CapitalStructureRowTitles.AvailableLiquidity,
        amount: captitalStructure.availableLiquidity,
        clientEbitda: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
        spread: null,
        maturity: null,
        agent: null,
        breakRow: false,
      },
    ];

    return captitalStructureRows.filter(_ => !!_) as Bixby.Models.CompanyCapitalStructureRow[];
  }

  public static buildManageCompanyTransactionRow(
    transactionDetails: Bixby.Models.DraftCompanyTransactionDetails
  ): Bixby.Models.CompanyTransactionDetailGridRow[] {
    const formatCurrency = (value: number | null | undefined, includeCents: boolean) => {
      if (value === null || value === undefined) {
        return null;
      }

      const parsedValue: number = parseFloat(value as any);
      if (isNaN(parsedValue)) {
        return null;
      }

      // add in appropriate formatting
      let prefix = '$';
      let suffix = '';
      if (parsedValue < 0) {
        prefix = prefix + '(';
        suffix = suffix + ')';
      }

      // remove hyphen
      const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

      let currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,');

      if (!includeCents) {
        currency = currency.split('.')[0];
      }

      return prefix + currency + suffix;
    };

    const formatMultiplier = (value: number | null | undefined) => {
      if (value === null || value === undefined) {
        return null;
      }

      const parsedValue: number = parseFloat(value as any);
      if (isNaN(parsedValue)) {
        return null;
      }

      const multiplier: string = parsedValue.toFixed(1);
      return multiplier + 'x';
    };

    return [
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TransactionDate,
        name: transactionDetails.transactionDateName,
        value: transactionDetails.transactionDate
          ? Bixby.Utils.formatMonthYear(dayjs.utc(transactionDetails.transactionDate).toDate())
          : '',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValue,
        name: transactionDetails.totalEnterpriseValueName,
        value: formatCurrency(transactionDetails.totalEnterpriseValue, false) || 'NA',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValueByRevenue,
        name: transactionDetails.totalEnterpriseValueByRevenueName,
        value: formatMultiplier(transactionDetails.totalEnterpriseValueByRevenue) || 'NM',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValueByAdjustedManagementEbitda,
        name: transactionDetails.totalEnterpriseValueByAdjustedManagementEbitdaName,
        value: formatMultiplier(transactionDetails.totalEnterpriseValueByAdjustedManagementEbitda) || 'NM',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.NetDebt,
        name: transactionDetails.netDebtName,
        value: formatCurrency(transactionDetails.netDebt, false) || 'NA',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEquity,
        name: transactionDetails.totalEquityName,
        value: formatCurrency(transactionDetails.totalEquity, false) || 'NA',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.CumulativeDividends,
        name: transactionDetails.cumulativeDividendsName,
        value: formatCurrency(transactionDetails.cumulativeDividends, false) || 'NA',
      },
      {
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.ImpliedEquityAtRisk,
        name: transactionDetails.impliedEquityAtRiskName,
        value: formatCurrency(transactionDetails.impliedEquityAtRisk, false) || 'NM',
      },
    ];
  }
}
